import { FormEvent, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { BookOpenText, Edit, Megaphone, Pin, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminApiError,
  AdminStoreAnnouncement,
  AdminStoreAnnouncementCategory,
  AdminStoreAnnouncementPayload,
  AdminStoreAnnouncementPriority,
  AdminStoreUserLocation,
  AdminUser,
  PaginationMeta,
  createAdminStoreAnnouncement,
  deleteAdminStoreAnnouncement,
  getCurrentAdmin,
  listAdminStoreAnnouncements,
  logout,
  updateAdminStoreAnnouncement,
} from "./api";
import Pagination from "./components/Pagination";

type FormState = {
  Title: string;
  Category: AdminStoreAnnouncementCategory;
  Priority: AdminStoreAnnouncementPriority;
  AudienceType: "All" | "Locations";
  Body: string;
  IsPinned: boolean;
  IsActive: boolean;
  PublishedAt: string;
  ExpiresAt: string;
  LocationIds: string[];
};

const defaultPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };
const emptyForm: FormState = {
  Title: "",
  Category: "Announcement",
  Priority: "Normal",
  AudienceType: "All",
  Body: "",
  IsPinned: false,
  IsActive: true,
  PublishedAt: "",
  ExpiresAt: "",
  LocationIds: [],
};

export default function AdminStoreAnnouncements() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminStoreAnnouncement[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState<PaginationMeta>(defaultPagination);
  const [locations, setLocations] = useState<AdminStoreUserLocation[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<{ Key: string; Label: string }[]>([]);
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [keyword, setKeyword] = useState("");
  const [filterLocationIds, setFilterLocationIds] = useState<string[]>([]);
  const [editingItem, setEditingItem] = useState<AdminStoreAnnouncement | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pageMessage, setPageMessage] = useState("");
  const [formMessage, setFormMessage] = useState("");

  const locationNameMap = useMemo(() => Object.fromEntries(locations.map((item) => [item.LocationId, item.Name])), [locations]);

  async function load(filters = { Status: status, Category: category, Keyword: keyword, LocationIds: filterLocationIds, Page: pagination.Page, PerPage: pagination.PerPage }) {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminStoreAnnouncements(filters);
      setItems(data.Items || []);
      setCounts(data.Counts || {});
      setPagination(data.Pagination || defaultPagination);
      setLocations(data.Locations || []);
      setCategoryOptions(data.CategoryOptions || []);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "載入失敗，請稍後再試。");
      if (error instanceof AdminApiError && error.status === 401) navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load({ Status: "", Category: "", Keyword: "", LocationIds: [], Page: 1, PerPage: defaultPagination.PerPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function openCreate() {
    setEditingItem(null);
    setForm(emptyForm);
    setFormMessage("");
    setFormOpen(true);
  }

  function toDateTimeLocal(value?: string | null) {
    return value ? value.replace(" ", "T").slice(0, 16) : "";
  }

  function openEdit(item: AdminStoreAnnouncement) {
    setEditingItem(item);
    setForm({
      Title: item.Title,
      Category: item.Category,
      Priority: item.Priority,
      AudienceType: item.AudienceType,
      Body: item.Body,
      IsPinned: item.IsPinned,
      IsActive: item.IsActive,
      PublishedAt: toDateTimeLocal(item.PublishedAt),
      ExpiresAt: toDateTimeLocal(item.ExpiresAt),
      LocationIds: item.LocationIds || [],
    });
    setFormMessage("");
    setFormOpen(true);
  }

  function payload(): AdminStoreAnnouncementPayload {
    return {
      Title: form.Title.trim(),
      Category: form.Category,
      Priority: form.Priority,
      AudienceType: form.AudienceType,
      Body: form.Body.trim(),
      IsPinned: form.IsPinned,
      IsActive: form.IsActive,
      PublishedAt: form.PublishedAt || null,
      ExpiresAt: form.ExpiresAt || null,
      LocationIds: form.AudienceType === "Locations" ? form.LocationIds : [],
    };
  }

  function validate() {
    if (!form.Title.trim()) return "請輸入標題。";
    if (!form.Body.trim()) return "請輸入公告內容。";
    if (form.AudienceType === "Locations" && form.LocationIds.length === 0) return "指定門市時，請至少選擇一間門市。";
    return "";
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const error = validate();
    if (error) {
      setFormMessage(error);
      return;
    }
    setSaving(true);
    setFormMessage("");
    try {
      if (editingItem) await updateAdminStoreAnnouncement(editingItem.StoreAnnouncementId, payload());
      else await createAdminStoreAnnouncement(payload());
      setFormOpen(false);
      await load({ Status: status, Category: category, Keyword: keyword.trim(), LocationIds: filterLocationIds, Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: AdminStoreAnnouncement) {
    if (!window.confirm(`確定刪除「${item.Title}」？刪除後門市端將看不到此公告。`)) return;
    setPageMessage("");
    try {
      await deleteAdminStoreAnnouncement(item.StoreAnnouncementId);
      await load({ Status: status, Category: category, Keyword: keyword.trim(), LocationIds: filterLocationIds, Page: pagination.Page, PerPage: pagination.PerPage });
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "刪除失敗。");
    }
  }

  function handleSearch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    load({ Status: status, Category: category, Keyword: keyword.trim(), LocationIds: filterLocationIds, Page: 1, PerPage: pagination.PerPage });
  }

  function toggleLocation(LocationId: string) {
    setForm((current) => ({
      ...current,
      LocationIds: current.LocationIds.includes(LocationId) ? current.LocationIds.filter((id) => id !== LocationId) : [...current.LocationIds, LocationId],
    }));
  }

  function toggleFilterLocation(LocationId: string) {
    setFilterLocationIds((current) => (current.includes(LocationId) ? current.filter((id) => id !== LocationId) : [...current, LocationId]));
  }

  return (
    <AdminLayout sectionLabel="門市管理" title="門市公告 SOP" user={currentUser} onLogout={handleLogout}>
      <form className="mb-5 grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3 sm:grid-cols-2 2xl:grid-cols-[170px_170px_minmax(300px,1fr)_auto] 2xl:items-end 2xl:p-4" onSubmit={handleSearch}>
        <Field label="狀態">
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="">全部</option>
            <option value="Active">啟用中</option>
            <option value="Inactive">已停用</option>
            <option value="Pinned">置頂</option>
          </select>
        </Field>
        <Field label="類型">
          <select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={category} onChange={(event) => setCategory(event.target.value)}>
            <option value="">全部</option>
            {categoryOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}
          </select>
        </Field>
        <Field className="sm:col-span-2 2xl:col-span-1 2xl:min-w-0" label="關鍵字">
          <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="標題、公告內容" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
        </Field>
        <div className="rwd-action-grid sm:col-span-2 2xl:col-span-1">
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32]" type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => load()} type="button"><RefreshCw size={18} />整理</button>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white hover:bg-[#203d32]" onClick={openCreate} type="button"><Plus size={18} />新增</button>
        </div>
        <div className="border-t border-[#edf0ee] pt-3 sm:col-span-2 2xl:col-span-4">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-sm font-bold text-[#59665f]">
            <span>門市篩選</span>
            <div className="flex gap-2 text-xs text-[#315746]">
              <button className="hover:underline" onClick={() => setFilterLocationIds(locations.map((item) => item.LocationId))} type="button">全選</button>
              <button className="hover:underline" onClick={() => setFilterLocationIds([])} type="button">清除</button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible">
            {locations.map((location) => (
              <label className={`flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-md border px-3 text-sm font-bold ${filterLocationIds.includes(location.LocationId) ? "border-[#9ac8aa] bg-[#f1f8f3] text-[#245438]" : "border-[#d8dedb] bg-white text-[#59665f]"}`} key={location.LocationId}>
                <input checked={filterLocationIds.includes(location.LocationId)} className="h-4 w-4 accent-[#315746]" onChange={() => toggleFilterLocation(location.LocationId)} type="checkbox" />
                {location.Name}
              </label>
            ))}
          </div>
        </div>
      </form>

      <div className="mb-4 grid gap-3 sm:grid-cols-2 2xl:grid-cols-4">
        <Summary label="全部公告" value={counts.Total || 0} />
        <Summary label="啟用中" value={counts.Active || 0} />
        <Summary label="置頂" value={counts.Pinned || 0} />
        <Summary label="已停用" value={counts.Inactive || 0} />
      </div>
      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="grid gap-3 p-3 lg:grid-cols-2 2xl:grid-cols-3">
          {loading ? (
            <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-8 text-center text-[#59665f] lg:col-span-2 2xl:col-span-3">載入中...</div>
          ) : items.length === 0 ? (
            <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-8 text-center text-[#59665f] lg:col-span-2 2xl:col-span-3">目前沒有符合條件的門市公告。</div>
          ) : items.map((item) => (
            <article className="grid gap-3 rounded-md border border-[#edf0ee] bg-white p-4" key={item.StoreAnnouncementId}>
              <div className="flex min-w-0 items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap gap-2">
                    <Badge>{item.CategoryLabel}</Badge>
                    {item.Priority === "High" && <Badge tone="warn">重要</Badge>}
                    {item.IsPinned && <Badge tone="green">置頂</Badge>}
                    <Badge tone={item.IsActive ? "green" : "muted"}>{item.IsActive ? "啟用" : "停用"}</Badge>
                  </div>
                  <h2 className="mt-3 break-words text-lg font-extrabold text-[#203d32]">{item.Title}</h2>
                </div>
                <BookOpenText className="shrink-0 text-[#315746]" size={22} />
              </div>
              <p className="line-clamp-3 whitespace-pre-line text-sm leading-6 text-[#59665f]">{item.Body}</p>
              <div className="grid gap-1 text-xs font-bold text-[#7b877f]">
                <span>對象：{item.AudienceType === "All" ? "全門市" : item.LocationIds.map((id) => locationNameMap[id] || `#${id}`).join("、")}</span>
                <span>發布：{item.PublishedAt || "立即"}　到期：{item.ExpiresAt || "無"}</span>
                <span>已讀紀錄：{item.ReadCount} 筆</span>
              </div>
              <div className="flex flex-wrap gap-2 border-t border-[#edf0ee] pt-3">
                <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => openEdit(item)} type="button"><Edit size={16} />編輯</button>
                <button className="inline-flex h-9 items-center gap-1 rounded-md border border-[#f0c7c4] bg-white px-3 text-sm font-bold text-[#9a2d2d] hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button"><Trash2 size={16} />刪除</button>
              </div>
            </article>
          ))}
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load({ Status: status, Category: category, Keyword: keyword.trim(), LocationIds: filterLocationIds, Page: page, PerPage: pagination.PerPage })} />
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-[#10251c]/35 sm:items-center sm:px-4 sm:py-6">
          <form className="flex max-h-[94vh] w-full max-w-[860px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.18)] sm:rounded-md" onSubmit={handleSubmit}>
            <div className="flex shrink-0 items-center justify-between border-b border-[#edf0ee] px-5 py-4">
              <div>
                <p className="text-xs font-bold tracking-[.16em] text-[#7b877f]">STORE ANNOUNCEMENT</p>
                <h2 className="text-xl font-bold text-[#203d32]">{editingItem ? "編輯門市公告" : "新增門市公告"}</h2>
              </div>
              <button aria-label="關閉" className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] hover:bg-[#f0f5f2]" onClick={() => setFormOpen(false)} type="button"><X size={18} /></button>
            </div>
            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {formMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{formMessage}</div>}
              <Field label="標題"><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" maxLength={160} value={form.Title} onChange={(event) => setForm((current) => ({ ...current, Title: event.target.value }))} /></Field>
              <div className="grid gap-4 sm:grid-cols-3">
                <Field label="類型"><select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3" value={form.Category} onChange={(event) => setForm((current) => ({ ...current, Category: event.target.value as AdminStoreAnnouncementCategory }))}>{categoryOptions.map((item) => <option key={item.Key} value={item.Key}>{item.Label}</option>)}</select></Field>
                <Field label="重要性"><select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3" value={form.Priority} onChange={(event) => setForm((current) => ({ ...current, Priority: event.target.value as AdminStoreAnnouncementPriority }))}><option value="Normal">一般</option><option value="High">重要</option></select></Field>
                <Field label="對象"><select className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3" value={form.AudienceType} onChange={(event) => setForm((current) => ({ ...current, AudienceType: event.target.value as "All" | "Locations" }))}><option value="All">全門市</option><option value="Locations">指定門市</option></select></Field>
              </div>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">內容<textarea className="min-h-[220px] rounded-md border border-[#cfd8d2] px-3 py-2 font-medium leading-7 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Body} onChange={(event) => setForm((current) => ({ ...current, Body: event.target.value }))} /></label>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="發布時間"><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="datetime-local" value={form.PublishedAt} onChange={(event) => setForm((current) => ({ ...current, PublishedAt: event.target.value }))} /></Field>
                <Field label="到期時間"><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="datetime-local" value={form.ExpiresAt} onChange={(event) => setForm((current) => ({ ...current, ExpiresAt: event.target.value }))} /></Field>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3 text-sm font-bold text-[#315746]"><input checked={form.IsPinned} className="h-4 w-4 accent-[#315746]" onChange={(event) => setForm((current) => ({ ...current, IsPinned: event.target.checked }))} type="checkbox" /><Pin size={16} />置頂顯示</label>
                <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3 text-sm font-bold text-[#315746]"><input checked={form.IsActive} className="h-4 w-4 accent-[#315746]" onChange={(event) => setForm((current) => ({ ...current, IsActive: event.target.checked }))} type="checkbox" /><Megaphone size={16} />啟用公告</label>
              </div>
              {form.AudienceType === "Locations" && (
                <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
                  <p className="mb-3 text-sm font-bold text-[#315746]">指定門市</p>
                  <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {locations.map((location) => <label className="flex cursor-pointer items-center gap-2 rounded-md border border-[#e3e9e5] bg-white px-3 py-2 text-sm font-bold text-[#315746]" key={location.LocationId}><input checked={form.LocationIds.includes(location.LocationId)} className="h-4 w-4 accent-[#315746]" onChange={() => toggleLocation(location.LocationId)} type="checkbox" />{location.Name}</label>)}
                  </div>
                </div>
              )}
            </div>
            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => setFormOpen(false)} type="button">取消</button>
              <button className="h-10 rounded-md bg-[#315746] px-5 font-bold text-white hover:bg-[#203d32] disabled:opacity-60" disabled={saving} type="submit">{saving ? "儲存中" : "儲存"}</button>
            </div>
          </form>
        </div>
      )}
    </AdminLayout>
  );
}

function Field({ children, className = "", label }: { children: ReactNode; className?: string; label: string }) {
  return <label className={`grid gap-1 text-sm font-bold text-[#59665f] ${className}`}><span>{label}</span>{children}</label>;
}

function Summary({ label, value }: { label: string; value: number }) {
  return <div className="rounded-md border border-[#dfe6e1] bg-white px-4 py-3"><p className="text-xs font-bold text-[#7b877f]">{label}</p><p className="mt-1 text-2xl font-bold text-[#203d32]">{value}</p></div>;
}

function Badge({ children, tone = "muted" }: { children: ReactNode; tone?: "muted" | "green" | "warn" }) {
  const className = tone === "green" ? "bg-[#e9f4ec] text-[#245438]" : tone === "warn" ? "bg-[#fff7e8] text-[#9a5b16]" : "bg-[#eef3f8] text-[#255071]";
  return <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${className}`}>{children}</span>;
}
